import {
  showAsFormattedRupeeAmount,
  formatPlanRupeeLabel,
  roundToNearestRupee,
  type PlanAmount
} from './lumpsumPlanAmount'

export type { PlanAmount }

/**
 * SIP monthly amounts — whole rupees, same figure for the bucket and the screen.
 * roundTargetSipToHundred is not used.
 *
 * Naming (lodash-style):
 * - formatTargetSip        → PlanAmount for recommended monthly SIP (.label / .value)
 * - formatTargetSipLabel   → string (display-only)
 * - formatSipIncreasePlan  → target + top-up PlanAmounts for increase sheet/CTA
 * - roundTargetSipToHundred → nearest ₹100; not applied
 */

/** Raw recommended SIP as whole rupees. The bucket reads .value. */
export function formatTargetSip({ rawAmount }: { rawAmount: number }): PlanAmount {
  const value = roundToNearestRupee(rawAmount)
  return { value, label: formatPlanRupeeLabel(value) }
}

/** Display-only. Use formatTargetSip when the same amount is executed. */
export function formatTargetSipLabel({ rawAmount }: { rawAmount: number }): string {
  return formatTargetSip({ rawAmount }).label
}

export type SipIncreasePlan = {
  /** Normalized target monthly SIP (sheet heading). */
  formattedTargetSip: PlanAmount
  /** Amount to add on PowerUp (folio copy + CTA). */
  formattedIncreaseAmount: PlanAmount
  /** Unrounded API delta — metadata only, not for UI or CTA. */
  rawIncreaseDifference: number
}

/**
 * Increase SIP sheet: builds both amounts from current folio total + raw API target.
 * Pass recommendation.recommendedSipAmount as rawTargetSip; sum folios for currentTotalSip.
 */
export function formatSipIncreasePlan({
  currentTotalSip,
  rawTargetSip
}: {
  currentTotalSip: number
  rawTargetSip: number
}): SipIncreasePlan {
  const formattedTargetSip = formatTargetSip({ rawAmount: rawTargetSip })
  const normalizedCurrentTotal = roundToNearestRupee(currentTotalSip)
  const increaseValue = Math.max(0, formattedTargetSip.value - normalizedCurrentTotal)

  return {
    formattedTargetSip,
    formattedIncreaseAmount: showAsFormattedRupeeAmount({ rawAmount: increaseValue }),
    rawIncreaseDifference: Math.max(0, rawTargetSip - currentTotalSip)
  }
}

/** Nearest ₹100. Not used for SIP buckets or SIP labels. */
export function roundTargetSipToHundred(rawAmount: number): number {
  if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
    return 0
  }
  return Math.round(rawAmount / 100) * 100
}
